import { getOpenStatus, type OperatingHours } from '@/lib/hours/openStatus'

/** Campos de pausa de `locations` (nuevos — best-effort, pueden faltar pre-migración). */
export type LocationPauseFields = {
  online_ordering_paused_at?: string | null
  online_ordering_paused_until?: string | null
  online_ordering_pause_reason?: string | null
}

export type PauseStatus = {
  /** Las ventas online están pausadas AHORA. */
  paused: boolean
  /** ISO del instante en que la pausa se levanta sola, o null si es manual. */
  resumesAt: string | null
  /** Nota opcional del operador. */
  reason: string | null
}

/**
 * Estado efectivo de la pausa de ventas online.
 *
 * Fuente de verdad única: se usa igual en el gate de dinero (session route), la
 * microsite pública y la UI admin, para que nunca discrepen.
 *
 * Auto-reanudar: si `paused_until` ya pasó (el local llegó a su cierre programado)
 * se considera NO pausado aunque `paused_at` siga seteado — así al reabrir en su
 * horario regular vuelve online sin que nadie toque nada. La fila se limpia
 * perezosamente en el próximo pause/resume.
 */
export function getOnlineOrderingPauseStatus(
  loc: LocationPauseFields | null | undefined,
  now: Date,
): PauseStatus {
  const pausedAt = loc?.online_ordering_paused_at ?? null
  if (!pausedAt) return { paused: false, resumesAt: null, reason: null }

  const until = loc?.online_ordering_paused_until ?? null
  if (until && new Date(until).getTime() <= now.getTime()) {
    // Ventana de auto-reanudar ya cumplida: el local cerró en su horario.
    return { paused: false, resumesAt: null, reason: null }
  }

  return {
    paused: true,
    resumesAt: until,
    reason: loc?.online_ordering_pause_reason ?? null,
  }
}

/**
 * Instante de auto-reanudar para una pausa nueva: el fin del intervalo abierto
 * actual (usa `minutesUntilClose`, que ya contempla cruce de medianoche).
 *
 * Devuelve null cuando el local está cerrado ahora o no tiene horario configurado
 * (siempre-abierto) — en esos casos la pausa es solo de reanudar manual, porque no
 * hay un "próximo cierre" del intervalo en curso.
 */
export function computePauseResumesAt(
  operatingHours: OperatingHours | null,
  timezone: string,
  now: Date,
): string | null {
  const status = getOpenStatus(operatingHours, timezone, 0, now)
  if (status.isOpen && status.minutesUntilClose != null) {
    return new Date(now.getTime() + status.minutesUntilClose * 60_000).toISOString()
  }
  return null
}
